import { Injectable } from '@angular/core';
import { PlanogramDrop } from '../models/layout/planogram-drop';
import { PlanogramLayout } from '../models/layout/planogram-layout';
import { PlanogramProduct } from '../models/layout/planogram-product';
import { PlanogramProductStack } from '../models/layout/planogram-product-stack';
import { PlanogramShelf } from '../models/layout/planogram-shelf';
import { DropSettings } from '../models/drop-settings';
import { DropAlignment } from '../models/layout/drop-alignment';
import { LayoutStateService } from './layout-state.service';

/**
 * Read-only queries over the current layout snapshot — drop/shelf/stack/product
 * lookups, facing counts, DOS lookups, and drop-settings extraction. Carved
 * out of LayoutService in Step 4 of the split.
 *
 * WARNING: getDrop / getShelves / getShelf / getStack / getProduct read
 * directly from `state.getLayoutValue()` — the live layout, NOT a clone.
 * They are safe for read-only callers (components, validators, breakdowns).
 *
 * DO NOT use them inside a mutator that has already called
 * `getCurrentLayout()` to obtain a working clone: any writes through these
 * refs land on the live source, while `updateLayout(clone)` then publishes
 * the clone (without those writes) and pushes the clone onto the undo
 * stack — a bug we already fixed once. Inside mutators, traverse the
 * cloned layout directly: `layout.drops[d]?.shelves[s]?.stacks[k]`.
 */
@Injectable({
  providedIn: 'root',
})
export class LayoutQueryService {
  constructor(private state: LayoutStateService) {}

  getCurrentLayout(deepclone: boolean = true): PlanogramLayout | null {
    return this.state.getCurrentLayout(deepclone);
  }

  getDrop(dropIndex: number): PlanogramDrop | undefined {
    const layout = this.state.getLayoutValue();
    if (!layout) return undefined;
    return layout.drops[dropIndex];
  }

  getShelf(dropIndex: number, shelfIndex: number): PlanogramShelf | undefined {
    const drop = this.getDrop(dropIndex);
    if (!drop) return undefined;
    return drop.shelves[shelfIndex];
  }

  getShelves(dropIndex: number): PlanogramShelf[] | undefined {
    const drop = this.getDrop(dropIndex);
    if (!drop) return undefined;
    return drop.shelves;
  }

  getStack(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number
  ): PlanogramProductStack | undefined {
    const shelf = this.getShelf(dropIndex, shelfIndex);
    if (!shelf) return undefined;
    return shelf.stacks[stackIndex];
  }

  getProduct(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    productIndex: number
  ): PlanogramProduct | undefined {
    const stack = this.getStack(dropIndex, shelfIndex, stackIndex);
    if (!stack) return undefined;
    return stack.products[productIndex];
  }

  getDropSettings(index: number): DropSettings | null {
    const layout = this.state.getLayoutValue();
    if (!layout) return null;

    const drop = layout.drops.at(index);
    if (!drop) return null;

    return {
      index: index,
      alignment: drop.alignment ?? DropAlignment.SPACE_BETWEEN,
      height: drop.height,
      width: drop.width,
      depth: drop.depth,
      footerHeight: drop.footerHeight,
      lipHeight: drop.lipHeight,
      minSpacing: drop.minSpacing,
      shelfSettings: drop.shelves.map((shelf, shelfIndex) => ({
        index: shelfIndex,
        depth: shelf.shelfDepth ?? null,
        minHeight: shelf.minHeight ?? null,
        text: shelf.text ?? null,
      })),
    };
  }

  getActualFacingCounts(): Map<number, number> {
    const facingCounts = new Map<number, number>();
    const layout = this.state.getLayoutValue();

    if (!layout) return facingCounts;

    this.state.planogramItems.forEach((product) => {
      facingCounts.set(product.productId, 0);
    });

    layout.drops.forEach((drop) => {
      drop.shelves.forEach((shelf) => {
        shelf.stacks.forEach((stack) => {
          stack.products.forEach((product) => {
            const currentCount = facingCounts.get(product.productId) || 0;
            facingCounts.set(product.productId, currentCount + 1);
          });
        });
      });
    });

    return facingCounts;
  }

  getProductsLinearUsedSpace(): Map<number, number> {
    const linearWidthMap = new Map<number, number>();
    this.state.planogramItems.forEach((product) => {
      linearWidthMap.set(product.productId, 0);
    });
    const layout = this.state.getLayoutValue();
    layout?.drops.forEach((drop) => {
      drop.shelves.forEach((shelf) => {
        shelf.stacks.forEach((stack) => {
          const uniqueProducts = new Set(
            stack.products.map((p) => p.productId)
          );
          uniqueProducts.forEach((productId) => {
            const product = stack.products.find((p) => p.productId == productId);
            const current = linearWidthMap.get(productId)!;
            linearWidthMap.set(productId, current + product!.dimensions!.width);
          });
        });
      });
    });
    return linearWidthMap;
  }

  getProductDOS(productId: number): number {
    return this.state.getDOSValue().get(productId) ?? 0;
  }

  /** The full live per-product DOS breakdown (productId -> total DOS). */
  getLayoutDOS(): Map<number, number> {
    return this.state.getDOSValue();
  }
}
